import { inject, Injectable, Renderer2, RendererFactory2, signal, effect, WritableSignal } from '@angular/core';
import { UserChatMessage } from '@llm/interfaces/interfaces_to_main_component';
import { AudioTranscriptionService } from '@llm/service/audio_transciption.service';
import {Observable, of, Subject, switchMap, timer} from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { UiStateService } from '@llm/service/signals_ui_state.service';
import {llmStoreService, RecordingStatus, MicrophonePermissionStatus} from '@llm/service/llm_store.service';
import {Tools_aiConsoleService} from "@llm/service/tools_ai-console.service";
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import {Rag_search_web} from "@llm/dialogs/rag_search_web/rag_search_web";
import {MatDialog} from "@angular/material/dialog";
import {SearchWebInfoTool} from '@llm/models/schemas/agents/search_info/searchWebInfo.service';

@Injectable({ providedIn: 'root' })
export class ChatUIService {
  //Injectables
  private store =inject(llmStoreService);
  private _console = inject(Tools_aiConsoleService);
  private _toolSearchWebInfo= inject(SearchWebInfoTool);
  private _dialog = inject(MatDialog);
  private chatContainer: HTMLElement | null = null;
  private renderer: Renderer2;
  private botAvatar = 'assets/llm_resources/img/robot_v1/robot-9580029-7746765.webp';
  private userAvatar = 'assets/llm_resources/img/userAvatar.jpg';
  private typingIndicator: HTMLElement | null = null;
  private containerReady = new Subject<HTMLElement>();
  public containerReady$ = this.containerReady.asObservable();
  public requestOpenChat = new Subject<void>();
  private trascriptionActually = signal<string>('')

  // WritableSignal to hold the reference to the chats container element
  // It is initialized to null and updated by the component.
  private _chatContainerElement: WritableSignal<HTMLElement | null> = signal(null);
  // Expose a readonly version for consumption, to prevent accidental direct modification
  public readonly chatContainerElement = this._chatContainerElement.asReadonly();

  // WritableSignal to hold the reference to the user text area input
  private _userTextAreaElement: WritableSignal<HTMLTextAreaElement | null> = signal(null);
  public readonly userTextAreaElement = this._userTextAreaElement.asReadonly();

  // WritableSignal to hold the reference to the send button
  private _sendButtonElement: WritableSignal<HTMLButtonElement | null> = signal(null);
  public readonly sendButtonElement = this._sendButtonElement.asReadonly();

  // WritableSignal to hold the reference to the widget element
  private _widgetElement: WritableSignal<HTMLElement | null> = signal(null);
  public readonly widgetElement = this._widgetElement.asReadonly();

  // WritableSignal to hold the reference to the profile div element
  private _profileDivElement: WritableSignal<HTMLElement | null> = signal(null);
  public readonly profileDivElement = this._profileDivElement.asReadonly();

  // WritableSignal to hold the reference to the animated image
  private _imgAnimatedElement: WritableSignal<HTMLImageElement | null> = signal(null);
  public readonly imgAnimatedElement = this._imgAnimatedElement.asReadonly();

  // WritableSignal to hold the reference to the static image
  private _imgStaticElement: WritableSignal<HTMLImageElement | null> = signal(null);
  public readonly imgStaticElement = this._imgStaticElement.asReadonly();

  // Subject para notificar cuando se debe enviar un mensaje
  public messageToSend = new Subject<string>();

  constructor(
    private audioTranscriptionService: AudioTranscriptionService,
    private _uiStateService: UiStateService,
    private rendererFactory: RendererFactory2
    ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);

      effect(() => {
          const newText = this.store.action_instant();

          if (this.typingTextElement && this.store.isExpandedForComplexContent()) {
              setTimeout(() => {
                  this.renderer.setProperty(
                      this.typingTextElement,
                      'textContent',
                      newText || ''
                  );
                  this.renderer.setStyle(
                      this.typingTextElement,
                      'margin-right',
                      '5vh'
                  );
              }, 1000); // 1000 ms = 1 segundo
          }
      });


    // Accediendo de forma reactiva a los elementos del DOM
    effect(() => {
      if (this._chatContainerElement()) {
        this.chatContainer = this._chatContainerElement();
      } else {
        this.chatContainer = null;
      }
    });

    effect(() => {
      const textArea = this._userTextAreaElement();
      if (textArea) {
        textArea.focus();
      } else {
      }
    }, {allowSignalWrites: true});

    effect(() => {
      const widget = this._widgetElement();
      if (widget) {
      } else {
      }
    });

    effect(() => {
      const profileDiv = this._profileDivElement();
      if (profileDiv) {
      } else {
      }
    });
  }

    private typingTextElement?: HTMLElement;

  // --- Methods to update element references from component ---

  setChatContainer(element: HTMLElement | null) {
    this._chatContainerElement.set(element);
  }

  setUserTextArea(element: HTMLTextAreaElement | null) {
    this._userTextAreaElement.set(element);
  }

  setSendButton(element: HTMLButtonElement | null) {
    this._sendButtonElement.set(element);
  }

  setWidgetElement(element: HTMLElement | null) {
    this._widgetElement.set(element);
  }

  setProfileDivElement(element: HTMLElement | null) {
    this._profileDivElement.set(element);
  }

  setImgAnimatedElement(element: HTMLImageElement | null) {
    this._imgAnimatedElement.set(element);
  }

  setImgStaticElement(element: HTMLImageElement | null) {
    this._imgStaticElement.set(element);
  }

  _setChatContainer(container: HTMLElement | null): void {
    this.chatContainer = container;
    if (container) {
      this.containerReady.next(container);
    }
  }

  private withChatContainer(callback: (container: HTMLElement) => void): void {
    if (this.chatContainer) {
      callback(this.chatContainer);
    } else {
      this.containerReady$.pipe(
        take(1),
        takeUntil(timer(5000))
      ).subscribe(container => {
        callback(container);
      });
    }
  }

  // Maneja completamente la respuesta del usuario
  handleUserResponse(message: string | Blob): UserChatMessage {
    if (typeof message === 'string') {
      return this.addTextUserMessage(message);
    } else {
      return this.addAudioUserMessage(message);
    }
  }

  private addTextUserMessage(message: string): UserChatMessage {
    if (!this.chatContainer){
      return this.createFallbackMessage(message, 'text');
    }

    const messageId = this.generateMessageId();
    const messageContainer = this.createUserMessageContainer(messageId);
    const avatar = this.createUserAvatar();
    const textElement = this.createTextElement(message);


    if(message !=this._uiStateService._isRenderedTranscription()){
      this.renderer.appendChild(messageContainer, textElement);
      this.renderer.appendChild(this.chatContainer, messageContainer);
    }else{
      this._uiStateService._isRenderedTranscription.set('')
    }


    this.scrollToBottom();

    return {
      id: messageId,
      role: 'user',
      type: 'text',
      content: message,
      timestamp: new Date()
    };
  }

  private addAudioUserMessage(audioBlob: Blob): UserChatMessage {
    if (!this.chatContainer) return this.createFallbackMessage('', 'audio');

    const messageId = this.generateMessageId();
    const audioUrl = URL.createObjectURL(audioBlob);
    const messageContainer = this.createUserMessageContainer(messageId);
    const avatar = this.createUserAvatar();
    const audioBubble = this.createAudioBubble(messageId);
    this.renderer.appendChild(messageContainer, audioBubble);
    this.renderer.appendChild(this.chatContainer, messageContainer);

    this.scrollToBottom();

    return {
      id: messageId,
      role: 'user',
      type: 'audio',
      content: '',
      audioUrl: audioUrl,
      audioBlob: audioBlob,
      transcriptionStatus: 'pending',
      timestamp: new Date()
    };
  }

  // Métodos de creación de elementos
  private createUserMessageContainer(id: string): HTMLElement {
    const container = this.renderer.createElement('div');
    this.renderer.addClass(container, 'message-container');
    this.renderer.addClass(container, 'user-side');
    this.renderer.setAttribute(container, 'id', id);
    return container;
  }

  private createUserAvatar(): HTMLElement {
    const avatar = this.renderer.createElement('img');
    this.renderer.addClass(avatar, 'userAvatar');
    this.renderer.setAttribute(avatar, 'src', this.userAvatar);
    this.renderer.setAttribute(avatar, 'alt', 'user photo');
    return avatar;
  }

  private createTextElement(text: string): HTMLElement {
    const textElement = this.renderer.createElement('p');
    this.renderer.addClass(textElement, 'userMsg');
    this.renderer.appendChild(textElement, this.renderer.createText(text));
    return textElement;
  }

  private createAudioBubble(messageId: string): HTMLElement {
    const bubble = this.renderer.createElement('div');
    this.renderer.addClass(bubble, 'userMsg');
    this.renderer.addClass(bubble, 'audio-message-bubble');

    const statusDiv = this.renderer.createElement('div');
    this.renderer.addClass(statusDiv, 'transcription-status');
    this.renderer.setAttribute(statusDiv, 'id', `status-${messageId}`);

    // Crear spinner
    const spinner = this.renderer.createElement('i');
    this.renderer.addClass(spinner, 'fas');
    this.renderer.addClass(spinner, 'fa-spinner');
    this.renderer.addClass(spinner, 'fa-spin');

    const statusText = this.renderer.createElement('span');
    this.renderer.appendChild(statusText, this.renderer.createText('Transcrevendo'));

    const loadingDots = this.renderer.createElement('span');
    this.renderer.addClass(loadingDots, 'loading-dots');
    for (let i = 0; i < 3; i++) {
      const dot = this.renderer.createElement('span');
      this.renderer.appendChild(dot, this.renderer.createText('.'));
      this.renderer.appendChild(loadingDots, dot);
    }

    this.renderer.appendChild(statusDiv, spinner);
    this.renderer.appendChild(statusDiv, statusText);
    this.renderer.appendChild(statusDiv, loadingDots);

    const textDiv = this.renderer.createElement('div');
    this.renderer.addClass(textDiv, 'transcription-text');
    this.renderer.setAttribute(textDiv, 'id', `text-${messageId}`);
    this.renderer.setStyle(textDiv, 'display', 'none');

    this.renderer.appendChild(bubble, statusDiv);
    this.renderer.appendChild(bubble, textDiv);

    return bubble;
  }

  // Transcribe audio y actualiza UI
  transcribeAndUpdateUI(audioMessage: UserChatMessage): void {
    this.store.isProcessingAudio.set(true);
    this.audioTranscriptionService.transcribeAudio(audioMessage.audioBlob!).subscribe({
      next: (transcript) => {
        this.updateAudioTranscriptionUI(audioMessage.id, transcript);
        this._uiStateService._isRenderedTranscription.set(transcript)
        this.messageToSend.next(transcript);

      },
      error: () => {
        this.updateAudioTranscriptionUI(audioMessage.id, '', true);
      },
      complete: () => {
        this.store.isProcessingAudio.set(false);
      }
    });
  }

  // Actualiza UI para transcripción de audio
  updateAudioTranscriptionUI(messageId: string, transcript: string, isError: boolean = false): void {
    //console.log('updateAudioTranscriptionUI...')
    this.withChatContainer((container) => {
      const messageContainer = container.querySelector(`#${messageId}`);
      if (!messageContainer) return;

      const statusElement = messageContainer.querySelector('.transcription-status');
      const textElement = messageContainer.querySelector('.transcription-text');

      if (!statusElement || !textElement) return;

      if (isError) {
        this.renderer.setProperty(statusElement, 'innerHTML', `
          <div class="transcription-error">
            <i class="fas fa-exclamation-triangle error-icon"></i>
            Error da transcrição
          </div>
        `);
        this.removeElementsByClass(['botTyping'])
        this.store.isProcessingAudio.set(false);
      } else {
        this.renderer.setStyle(statusElement, 'display', 'none');
        this.renderer.setStyle(textElement, 'display', 'block');
        this.renderer.setProperty(textElement, 'textContent', transcript);
      }
    });
  }


  // Maneja completamente la respuesta del bot
    handleBotResponse(message: { id: string; content: string | null }): void {
      this.hideTypingIndicator();
        this.withChatContainer((container) => {
            const existingElement = container.querySelector(`#${message.id}`);
            let response = message.content;
            let id_message = message.id;
            if (existingElement) {
                this._console.warn(`Se intentó renderizar un mensaje duplicado con ID: ${message.id}. Operación cancelada.`);
                return;
            }
            if (response === null) {
                response = 'Ocorreu um erro desconhecido ou a resposta foi nula.';
            }
            if (this._uiStateService.isNovoMessage()) {

                const messageContainer = this.renderer.createElement('div');
                this.renderer.setAttribute(messageContainer, 'id', id_message);
                this.renderer.addClass(messageContainer, 'message-container');

                const avatar = this.renderer.createElement('img');
                this.renderer.addClass(avatar, 'botAvatar');
                this.renderer.setAttribute(avatar, 'src', this.botAvatar);
                this.renderer.setAttribute(avatar, 'alt', 'bot profile');

                const message = this.renderer.createElement('p');
                this.renderer.addClass(message, 'botMsg');

                // Convertir Markdown a HTML seguro
                const html = marked.parse(response || '', { gfm: true, breaks: true });
                const safeHtml = DOMPurify.sanitize(html);
                this.renderer.setProperty(message, 'innerHTML', safeHtml);

                // Botón icono
                const iconBtn = this.renderer.createElement('button');
                this.renderer.addClass(iconBtn, 'print-icon-btn');
                this.renderer.setProperty(iconBtn, 'innerHTML',  '<i class="fas fa-clipboard-list"></i>');

                const clearfix = this.renderer.createElement('div');
                this.renderer.addClass(clearfix, 'clearfix');

                this.renderer.appendChild(messageContainer, message);
                this.renderer.appendChild(messageContainer, clearfix);

                this.renderer.appendChild(container, messageContainer);

            } else {
                const botMessages = container.getElementsByClassName('botMsg');
                if (botMessages.length > 0) {
                    const lastMessage = botMessages[botMessages.length - 1];
                    const html = marked.parse(response || '', { gfm: true, breaks: true });
                    const safeHtml = DOMPurify.sanitize(html);
                    this.renderer.setProperty(lastMessage, 'innerHTML', safeHtml);
                }
            }

            this.scrollToBottom();
        });
    }


    /**
     * Maneja la respuesta del bot, agregando el contenido de búsqueda web si está disponible.
     *
     * @param response La respuesta principal del bot (puede ser nula).
     */
    handleBotComplexResponse(response: string): void {
        this.hideTypingIndicator();

        this.withChatContainer((container) => {
            if (response !== '') {
                const messageContainer = this.renderer.createElement('div');
                this.renderer.addClass(messageContainer, 'message-container');

                // Crea un contenedor para el contenido markdown
                const markdownContent = this.renderer.createElement('div');
                this.renderer.addClass(markdownContent, 'bot-markdown-content');

                // Convierte el Markdown a HTML y lo sanitiza
                const html = marked.parse(response, { gfm: true, breaks: true });
                const safeHtml = DOMPurify.sanitize(html);
                this.renderer.setProperty(markdownContent, 'innerHTML', safeHtml);

                this.renderer.appendChild(messageContainer, markdownContent);
                this.renderer.appendChild(container, messageContainer);

            } else {
                const botMessages = container.getElementsByClassName('botMsg');
                if (botMessages.length > 0) {
                    const lastMessage = botMessages[botMessages.length - 1];
                    const html = marked.parse(response || '', { gfm: true, breaks: true });
                    const safeHtml = DOMPurify.sanitize(html);
                    this.renderer.setProperty(lastMessage, 'innerHTML', safeHtml);
                }
            }

            this.scrollToBottom();
        });
    }

  showTypingIndicator(): void {
    this.withChatContainer((container) => {
      this.hideTypingIndicator();

      const indicatorContainer = this.renderer.createElement('div');
      this.renderer.addClass(indicatorContainer, 'message-container');
      this.renderer.addClass(indicatorContainer, 'bot-message-container');
      this.renderer.setAttribute(indicatorContainer, 'id', 'botTypingContainer');

      const avatar = this.renderer.createElement('img');
      this.renderer.addClass(avatar, 'botAvatar');
      this.renderer.setAttribute(avatar, 'id', 'botTypingAvatar');
      this.renderer.setAttribute(avatar, 'src', this.botAvatar);
      this.renderer.setAttribute(avatar, 'alt', 'Avatar del Bot escribiendo');

        // 📌 Texto antes de los puntitos
        const textTypingIndicator = this.renderer.createElement('span');
        this.typingTextElement = textTypingIndicator;
        this.renderer.addClass(textTypingIndicator, 'botTypingText');
        this.renderer.addClass(textTypingIndicator, 'chat_header_title');

        this.renderer.setStyle(textTypingIndicator, 'font-size', '1em');
        this.renderer.setStyle(textTypingIndicator, 'font-style', 'italic');

        // Leemos el valor inicial del signal
        const currentText = this.store.action_instant();
        if (currentText) {
            const textNode = this.renderer.createText(currentText);
            this.renderer.appendChild(textTypingIndicator, textNode);
        }


      const typingIndicator = this.renderer.createElement('div');
      this.renderer.addClass(typingIndicator, 'botTyping');
      this.renderer.appendChild(typingIndicator, textTypingIndicator);

      for (let i = 1; i <= 3; i++) {
        const dot = this.renderer.createElement('div');
        this.renderer.addClass(dot, `bounce${i}`);
        this.renderer.appendChild(typingIndicator, dot);
      }

      //this.renderer.appendChild(indicatorContainer, avatar);

      this.renderer.appendChild(indicatorContainer, typingIndicator);
      this.renderer.appendChild(container, indicatorContainer);

      this.typingIndicator = indicatorContainer;
      this.scrollToBottom();

    });
  }

  hideTypingIndicator(): void {
    this.withChatContainer((container) => {
      if (this.typingIndicator) {
        this.renderer.removeChild(container, this.typingIndicator);
        this.typingIndicator = null;
      }
    });
  }

  // Limpia el chat
  clearChat(): void {
    this.withChatContainer((container) => {
      this.renderer.setProperty(container, 'innerHTML', '');
    });
  }

  // Genera ID único para mensajes
  private generateMessageId(): string {
    return `msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }

  clearChatContainer(): void {
    this.chatContainer = null;
  }

  // Scroll al final del chat
  public scrollToBottom(): void {
    setTimeout(() => {
      if (this.chatContainer) {
        try {
          this.chatContainer.scrollTop = this.chatContainer.scrollHeight;
        } catch (e) {
          this._console.error('Error scrolling to bottom:', e);
        }
      }
    }, 0);
  }

  public removeElementsByClass(classNames: string[]): void {
    this.withChatContainer((container) => {
      classNames.forEach(className => {
        const elements = container.getElementsByClassName(className);
        while (elements.length > 0) {
          elements[0].remove();
        }
      });
    });
  }

  private createFallbackMessage(content: string, type: 'text' | 'audio'): UserChatMessage {
    return {
      id: `fallback-${Date.now()}`,
      role: 'user',
      type: type,
      content: type === 'text' ? content : '',
      timestamp: new Date(),
      ...(type === 'audio' ? {
        audioUrl: '',
        audioBlob: new Blob(),
        transcriptionStatus: 'pending'
      } : {})
    };
  }

  /**
   * Retorna o texto apropriado para o tooltip com base no estado do botão (habilitado/desabilitado)
   * e no estado da funcionalidade de voz.
   * @returns {string} O texto a ser exibido no tooltip.
   */
  get tooltipDoBotaoDeConfiguracoes(): string {
    if (!this._uiStateService.isHabilitedButtonSettings()) {
      return 'A funcionalidade de configuraçoes está desabilitada no momento.';
    }
    return "Configuraçoes de GlorIA";
  }



    /**
     * Abre un diálogo para que el usuario seleccione un dato de una lista.
     * Utilizado cuando una búsqueda de datos devuelve múltiples resultados.
     * @param _data Array de objetos any para mostrar en el diálogo.
     * @returns Un Observable que emite el ID del dato seleccionado o el ID del dato por defecto.
     */
    openRag_search_webDialog(_data: { markdownText: string }): Observable<string> {
        const dialogRef = this._dialog.open(Rag_search_web, {
            width: '600px',
            data: _data
        });

        return dialogRef.afterClosed().pipe(
            switchMap(result => result ? of(result.toString()) : of('defaultItem'))
        );
    }
}
